import { type ComponentProps } from 'react'
import { Field as BaseField } from '@base-ui/react/field'
import { twMerge } from 'tailwind-merge'
import { itemDescriptionVariants } from '../../item/styles'
import { FIELD_DISABLED_TEXT } from './styles'
import type { PartProps } from '../../../lib/part-props'

type FieldErrorMatch = ComponentProps<typeof BaseField.Error>['match']

interface FieldNoteProps extends PartProps<ComponentProps<typeof BaseField.Description>> {
  className?: string
  /**
   * Render as the field's error rather than its help text, so it appears only while the control is
   * invalid. An error is never colour alone: this carries the message the red edge points at.
   */
  error?: boolean
  /**
   * Narrow an error to one `ValidityState` key, e.g. `valueMissing` or `typeMismatch`, so a field
   * can answer each way of being wrong with its own sentence. `true` always shows it, for when an
   * outside form library owns validity. Ignored unless `error` is set.
   */
  match?: FieldErrorMatch
}

/** Help text at rest, the error message when `error` is set. `text-xs` at every field size. */
function FieldNote({ className, error, match, children, ...props }: FieldNoteProps) {
  const classes = twMerge(
    // The same type as Item.Description, so a Field and an Item read as one list.
    itemDescriptionVariants({ tone: error ? 'danger' : 'default' }),
    // An invalid field turns its notes red too, so the help text reads as part of the error.
    !error && 'group-data-[invalid]/field:text-danger-subtle',
    FIELD_DISABLED_TEXT,
    className,
  )

  if (error) {
    return (
      <BaseField.Error data-slot="field-note" match={match} className={classes} {...props}>
        {children}
      </BaseField.Error>
    )
  }

  return (
    <BaseField.Description data-slot="field-note" className={classes} {...props}>
      {children}
    </BaseField.Description>
  )
}

export { FieldNote }
export type { FieldNoteProps }
